export const projectRules = {
	"cloudflare/fl/fl2": {
		mergeRequestFields: `
	        author {
	            groupMemberships {
                nodes {
                    group {
                        id
                    }
                }
            }
        }
	        mergeableDiscussionsState
        project {
            group {
                id
            }
        }
        labels {
            nodes {
                title
            }
        }
        commitCount
	        diffStats {
	          path
	        }
	    `,
		batchedMergeRequestFields: {
			batchSize: 5,
			fields: `
		        approvalState {
		            rules {
		                name
		                section
		                approved
		                groups {
		                    nodes {
		                        id
		                        name
		                    }
		                }
		            }
		        }
		    `,
		},
		additionalRules: (mr) => {
			if (!("projectFields" in mr)) {
				return ["missing project-specific MR metadata"];
			}
			const problems = [];
			const notes = [];
			const projectGroupId = mr.projectFields.project.group.id;
			const authorGroups = mr.projectFields.author.groupMemberships.nodes.map(
				(x) => x.group.id,
			);
			const authorIsProjectMember = authorGroups.includes(projectGroupId);

			if (!authorIsProjectMember) {
				// Ensure all module files are covered by CODEOWNERS, and hint
				// to FL what specific files require their attention
				const neededFiles = mr.projectFields.diffStats.map((x) => x?.path);
				const pathsNotNeedingProjectApproval =
					mr.projectFields.approvalState.rules
						.filter(
							(x) =>
								x.section === "CODEOWNERS" &&
								(x.groups.nodes.length === 0 ||
									x.groups.nodes.find((x) => x.id !== projectGroupId)),
						)
						// names may have a leading /, files do not
						.map((x) => (x.name.startsWith("/") ? x.name.substring(1) : x.name))
						// names may also have a trailing *, files do not
						.map((x) => (x.endsWith("*") ? x.substring(0, x.length - 1) : x));
				let hasCodeownersUpdate = false;
				let hasProjectTeamNeededFiles = false;
				const shouldNotNeedApprovalFiles = [];

				for (const neededFile of neededFiles) {
					if (neededFile === "CODEOWNERS") {
						hasCodeownersUpdate = true;
					}

					let found = false;
					for (const path of pathsNotNeedingProjectApproval) {
						if (neededFile.startsWith(path)) {
							found = true;
							break;
						}
					}
					if (!found) {
						if (
							neededFile.startsWith("feature-defaults") ||
							neededFile.startsWith("lib/module-") ||
							neededFile.startsWith(
								"tests/acceptance_tests/scripts/fl2/modules",
							) ||
							neededFile.startsWith("tests/integration/src/tests/module_")
						) {
							shouldNotNeedApprovalFiles.push(neededFile);
						} else {
							hasProjectTeamNeededFiles = true;
							notes.push(`FL must review: ${neededFile}`);
						}
					}
				}

				if (!hasProjectTeamNeededFiles && !shouldNotNeedApprovalFiles) {
					notes.push("This MR does not require FL team review");
				}

				if (shouldNotNeedApprovalFiles) {
					if (hasCodeownersUpdate) {
						for (const file of shouldNotNeedApprovalFiles) {
							notes.push(
								`Ensure ${file} is included in the CODEOWNERS update of this MR`,
							);
						}
					} else {
						for (const file of shouldNotNeedApprovalFiles) {
							problems.push(`CODEOWNERS update required for ${file}`);
						}
					}
				}

				// Ensure all other approvals are in place first
				const neededNonProjectGroupApprovals = [
					...new Set(
						mr.projectFields.approvalState.rules
							.filter(
								(x) =>
									!(
										x.approved ||
										x.groups.nodes.length === 0 ||
										(x.groups.nodes.length === 1 &&
											x.groups.nodes[0].id === projectGroupId)
									),
							)
							.flatMap((x) =>
								x.groups.nodes
									.filter((x) => x.id !== projectGroupId)
									.map((x) => x.name),
							),
					),
				].join(", ");

				if (neededNonProjectGroupApprovals) {
					problems.push(
						`Other teams must first review: ${neededNonProjectGroupApprovals}`,
					);
				}
			}

			if (!mr.projectFields.mergeableDiscussionsState) {
				problems.push("All discussions must be resolved before FL team review");
			}

			if (mr.conflicts) {
				problems.push("Merge conflicts must be resolved before FL team review");
			}

			if (
				mr.headPipeline?.failedJobs > 0 &&
				mr.headPipeline?.status !== "success"
			) {
				problems.push("Pipeline must be passing before FL team review");
			}

			if (mr.projectFields.commitCount > 1) {
				notes.push(
					`${mr.projectFields.commitCount} commits, consider squashing`,
				);
			}

			if (
				mr.projectFields.labels?.nodes?.find(
					(label) => label.title === "force-fl-review",
				)
			) {
				return {
					info: ["FL review explicitly requested, overriding blocks"]
						.concat(notes)
						.concat(problems),
				};
			}

			return { blocking: problems, info: notes };
		},
	},
};
